import { ColDef, ProcessCellForExportParams } from "ag-grid-community";

import { SideLoadedBusinessActionsRenderer } from "lib/agGrid/renderers/sideLoadedBusinessActionsRenderer";
import { SideLoadedBusinessIsUsedRenderer } from "lib/agGrid/renderers/sideLoadedBusinessIsUsedRenderer";
import { SideLoadedBusinessValidityRenderer } from "lib/agGrid/renderers/sideLoadedBusinessValidityRenderer";
import {
  sideLoadedMatchedFieldsTooltipValueGetter,
  sideLoadedPicsTooltipValueGetter,
  sideLoadedPicsValueGetter,
} from "lib/agGrid/getters/sideLoadedBusinesses";
import { SideLoadedBusinessRow } from "constants/sideLoadedBusinesses";

export const SideLoadedBusinessColId = {
  NAME: "ffe17918-8a30-439d-898a-b26e2ec36e99",
  ABN: "ce7b63f4-fb5d-4ec4-bdb0-240787f542fe",
  PICS: "2071eadb-b41a-48ee-b1ea-dbb41aa8eec9",
  BRANDS: "ce743f1f-b77a-4d1a-b43f-1fb77a3d1ac3",
  EMAIL: "9320ef08-209f-4731-a0ef-08209f6731f3",
  SOURCE_FILE: "28d79fe3-f34e-41d1-979f-e3f34e61d102",
  VALID: "455a6480-f0f3-4ac4-8e8b-611f4278a944",
  USED: "51ac6bcd-8670-4487-9ff3-79aaaebfb22f",
  IGNORED: "2f0d1c47-6a55-42f0-9b1e-4c8e6d3a7b21",
  ACTIONS: "6421925c-2a6b-4508-ae36-0cc873ecbea9",
};

// Group rows (from the Manage grid's dataset grouping) have no record of
// their own, so these must tolerate an undefined data.
const getUsedFilterValueGetter = (params: { data?: SideLoadedBusinessRow }) =>
  params.data?.sideLoadedBusiness.businessIds.length ? "Yes" : "No";

const getValidValueGetter = (params: { data?: SideLoadedBusinessRow }) => {
  const validation = params.data?.getValidation();
  if (!validation) {
    return null;
  }
  return validation.isValid ? "Valid" : "Invalid";
};

const getIgnoredFilterValueGetter = (params: {
  data?: SideLoadedBusinessRow;
}) => (params.data?.sideLoadedBusiness.isIgnored ? "Yes" : "No");

// Exports a record's issues, blank when valid, rather than Valid/Invalid.
export const processSideLoadedBusinessCellForExport = (
  params: ProcessCellForExportParams<SideLoadedBusinessRow>,
): string => {
  if (params.column.getColId() === SideLoadedBusinessColId.VALID) {
    return (
      params.node?.data?.getValidation().getValidationIssues().join(", ") ?? ""
    );
  }
  return params.value == null ? "" : String(params.value);
};

export const SideLoadedBusinessColumnDef: Record<
  string,
  ColDef<SideLoadedBusinessRow>
> = {
  [SideLoadedBusinessColId.NAME]: {
    colId: SideLoadedBusinessColId.NAME,
    field: "sideLoadedBusiness.name",
    headerName: "Name",
    flex: 2,
    minWidth: 180,
    tooltipValueGetter: sideLoadedMatchedFieldsTooltipValueGetter,
  },
  [SideLoadedBusinessColId.ABN]: {
    colId: SideLoadedBusinessColId.ABN,
    field: "sideLoadedBusiness.abn",
    headerName: "ABN",
    width: 140,
  },
  [SideLoadedBusinessColId.VALID]: {
    colId: SideLoadedBusinessColId.VALID,
    valueGetter: getValidValueGetter,
    headerName: "Valid",
    headerTooltip:
      "Whether this suggestion's Name, ABN, PICs and Email pass validation",
    width: 100,
    cellRenderer: SideLoadedBusinessValidityRenderer,
    // Otherwise the global search validates every row.
    getQuickFilterText: () => "",
    tooltipValueGetter: params =>
      params.data?.getValidation().getValidationIssues().join(", ") ||
      undefined,
  },
  [SideLoadedBusinessColId.PICS]: {
    colId: SideLoadedBusinessColId.PICS,
    field: "sideLoadedBusiness.pics",
    headerName: "Properties",
    width: 140,
    valueGetter: sideLoadedPicsValueGetter,
    tooltipValueGetter: sideLoadedPicsTooltipValueGetter,
  },
  [SideLoadedBusinessColId.BRANDS]: {
    colId: SideLoadedBusinessColId.BRANDS,
    field: "sideLoadedBusiness.brands",
    headerName: "Brands",
    width: 140,
  },
  [SideLoadedBusinessColId.EMAIL]: {
    colId: SideLoadedBusinessColId.EMAIL,
    field: "sideLoadedBusiness.email",
    headerName: "Email",
    width: 180,
  },
  // Grouped in the Manage grid, so it shows a label disambiguating two
  // same-named data sets - datasetName alone would merge them into one group.
  [SideLoadedBusinessColId.SOURCE_FILE]: {
    colId: SideLoadedBusinessColId.SOURCE_FILE,
    field: "datasetLabel",
    headerName: "Source",
    flex: 1,
    minWidth: 140,
    enableRowGroup: true,
  },
  [SideLoadedBusinessColId.USED]: {
    colId: SideLoadedBusinessColId.USED,
    field: "sideLoadedBusiness.businessIds",
    headerName: "Used",
    width: 90,
    cellRenderer: SideLoadedBusinessIsUsedRenderer,
    filterValueGetter: getUsedFilterValueGetter,
  },
  [SideLoadedBusinessColId.IGNORED]: {
    colId: SideLoadedBusinessColId.IGNORED,
    headerName: "Ignored",
    width: 100,
    valueGetter: getIgnoredFilterValueGetter,
  },
  [SideLoadedBusinessColId.ACTIONS]: {
    colId: SideLoadedBusinessColId.ACTIONS,
    headerName: "Actions",
    // A floor for the first frame only, well below what autosize will measure.
    minWidth: 100,
    pinned: "right",
    cellRenderer: SideLoadedBusinessActionsRenderer,
  },
};

export const SideLoadedBusinessColumnDefs = [
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.NAME],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.ABN],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.VALID],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.PICS],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.BRANDS],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.EMAIL],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.SOURCE_FILE],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.USED],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.IGNORED],
  SideLoadedBusinessColumnDef[SideLoadedBusinessColId.ACTIONS],
];

// Group rows carry more actions than leaf rows, and Ignore/Restore differ in
// width, so no fixed width fits every case.
export const SideLoadedBusinessAutoSizedColumnIds = [
  SideLoadedBusinessColId.ACTIONS,
];
